<i18n>
{
  "en": {
    "refreshTokenSuccess": "Refresh token success",
    "refreshTokenError": "Refresh token error",
    "posListError": "Error loading points of sale list. Try it later.",
    "modelLanguageListError": "Error loading model languages list. Try it later."
  },
  "es": {
    "refreshTokenSuccess": "Éxito refrescando token",
    "refreshTokenError": "Error refrescando token:",
    "posListError": "Error cargando la lista de restaurantes. Inténtelo de nuevo más tarde.",
    "modelLanguageListError":  "Error cargando la lista de lenguajes. Inténtelo de nuevo más tarde."
  }
}
</i18n>

<template>
  <fragment>
    <dynameat-header v-if="permissionsReady"/>
    <v-main>
      <router-view v-if="permissionsReady"/>
      <dynameat-footer/>
    </v-main>
  </fragment>
</template>

<script>
import { REFRESH_TOKEN_FREQUENCY } from '@dynameat/constants';
import { HttpController, SessionController } from '@core/helpers';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import Component from 'vue-class-component';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import * as api from '@dynameat/api';
import DynameatFooter from '@dynameat/components/DynameatFooter.vue';
import { PermissionController } from '@core/permissions';
import { Watch } from 'vue-property-decorator';
import DynameatHeader from './components/DynameatHeader.vue';

@Component
@Components({ DynameatHeader, DynameatFooter })
@MapGetters(['isLoading', 'selectedPointOfSale', 'moduleStatus'])
@MapActions(['addNotification', 'pointOfSaleListRequest', 'setLoading', 'groupTypeListRequest', 'modelLanguageRequest',
  'setSelectedPointOfSaleGuid', 'profileCompletionListRequest', 'userSettingsRequest', 'optionListRequest'])
export default class Dynameat extends Vue {
  permissionsReady = false;

  beforeRouteEnter(to, from, next) {
    if (!PermissionController.getInstance().isPosAllowed(to.params)) {
      next({ name: 'not-found-page', params: { 0: '' } });
    }
    next(async (vm) => {
      // access to component instance via `vm`
      HttpController.setupHttpClient(SessionController.token);
      await vm.refreshSession();
      setInterval(vm.refreshSession, REFRESH_TOKEN_FREQUENCY);

      // Setup point of sale selected
      await vm.setLoading(1);
      await vm.userSettingsRequest();
      await vm.optionListRequest();
      await vm.groupTypeListRequest();
      await vm.loadPointOfSaleList();
      await vm.loadModelLanguageList();
      if (!PermissionController.getInstance().isPosAllowed(to.params)) {
        next({ name: 'not-found-page', params: { 0: '' } });
      } else {
        await vm.setSelectedPointOfSaleGuid(vm.$route.params.pointOfSaleGuid);
        // noinspection ES6MissingAwait
        vm.profileCompletionListRequest();
        await vm.setLoading(-1);
      }
      vm.permissionsReady = true;
    });
  }

  async refreshSession() {
    if (this.$route.name !== 'login') {
      try {
        if (SessionController.needRefreshSession() && !SessionController.token) {
          await this.$store.dispatch('logout');
          await this.$router.push('/login');
        } else if (SessionController.needRefreshSession()) {
          const newAccessRaw = await api.refreshTokenRequest(SessionController.refresh);
          const newToken = `Bearer ${newAccessRaw}`;
          SessionController.token = newToken;
          SessionController.timestamp = new Date().getTime();
          HttpController.setupHttpClient(newToken);
        }
      } catch (err) {
        this.setLoading(1);
        this.addNotification(createErrorNotification(this.$t('refreshTokenError'), {
          onRemove: (async () => {
            this.setLoading(-1);
            await this.$store.dispatch('logout');
            await this.$router.push('/login');
          })
        }, 5000));
      }
    }
  }

  async loadModelLanguageList() {
    try {
      await this.modelLanguageRequest();
    } catch (err) {
      console.log(err);
      this.addNotification(createErrorNotification(this.$t('modelLanguageListError')));
    }
  }

  async loadPointOfSaleList() {
    try {
      await this.pointOfSaleListRequest();
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('posListError')));
    }
  }

  @Watch('selectedPointOfSale')
  onSelectedPointOfSaleChange(newPointOfSale, oldPointOfSale) {
    if (newPointOfSale.id !== oldPointOfSale.id) {
      PermissionController.getInstance().addModules(newPointOfSale.availableFrontGroups);
    }
  }

  @Watch('moduleStatus')
  onModuleStatus(newStatus) {
    if (newStatus) {
      if (this.$route.name && !PermissionController.getInstance().isAllowed(this.$route.name)) {
        this.$router.push({ name: 'restaurant-management' });
      }
    }
  }
}
</script>

<style lang="scss">
@import "./styles/dynameat.css";
</style>
